import React from 'react';
import { Banknote, CheckCircle, AlertCircle, Users } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { formatMontant, formatNumber, getTauxRecouvrement } from './recouvrementUtils';

// Cartes de statistiques calquees sur MaCaisse : fond pastel + border-2,
// pastille d'icone, puis encadre blanc interne avec lignes et ligne de total.
const RecouvrementStatsCards = ({ synthese, classes, eleves, isLoading }) => {
  const montantAttendu = Number(synthese.montant_attendu || 0);
  const montantPaye = Number(synthese.montant_paye || 0);
  const resteARecouvrer = Number(synthese.reste_a_recouvrer || 0);
  const elevesConcernes = Number(synthese.eleves_concernes || 0);
  const taux = getTauxRecouvrement(montantPaye, montantAttendu);

  const nbClasses = classes.length;
  const nbSections = new Set(classes.map((c) => c.section_id)).size;
  const resteMoyen = elevesConcernes > 0 ? resteARecouvrer / elevesConcernes : 0;
  const elevesSansTelephone = eleves.filter((e) => !e.parent_telephone).length;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 px-6">
      {/* Montant attendu */}
      <Card className="bg-blue-50 border-2 border-blue-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center">
              <Banknote size={22} className="text-blue-600" />
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Montant attendu</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-blue-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Élèves concernés</span>
              <span className="font-semibold text-gray-700 text-right">{formatNumber(elevesConcernes)}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Classes touchées</span>
              <span className="font-semibold text-gray-700 text-right">{formatNumber(nbClasses)}</span>
            </div>
            <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Total attendu</span>
              <span className="text-xl font-bold text-blue-700 text-right">{formatMontant(montantAttendu)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Déjà encaissé */}
      <Card className="bg-green-50 border-2 border-green-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle size={22} className="text-green-600" />
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Déjà encaissé</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-green-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Taux de recouvrement</span>
              <span className="font-semibold text-green-600 text-right">{taux}%</span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full bg-green-500" style={{ width: `${taux}%` }} />
            </div>
            <div className="border-t border-green-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Total encaissé</span>
              <span className="text-xl font-bold text-green-600 text-right">{formatMontant(montantPaye)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reste à recouvrer */}
      <Card className="bg-orange-50 border-2 border-orange-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertCircle size={22} className="text-orange-600" />
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Reste à recouvrer</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-orange-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Part non encaissée</span>
              <span className="font-semibold text-orange-600 text-right">{100 - taux}%</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Reste moyen par élève</span>
              <span className="font-semibold text-gray-700 text-right">{formatMontant(resteMoyen)}</span>
            </div>
            <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between items-center gap-3">
              <span className="text-gray-700 font-semibold">Total à recouvrer</span>
              <span className={resteARecouvrer > 0 ? 'text-xl font-bold text-[#c66d59] text-right' : 'text-xl font-bold text-green-600 text-right'}>
                {formatMontant(resteARecouvrer)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dossiers à suivre */}
      <Card className="bg-purple-50 border-2 border-purple-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="w-11 h-11 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users size={22} className="text-purple-600" />
            </div>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-3 font-semibold">Dossiers à suivre</p>
          <div className="space-y-2 bg-white rounded-lg p-3 border border-purple-100">
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Sections touchées</span>
              <span className="font-semibold text-gray-700 text-right">{formatNumber(nbSections)}</span>
            </div>
            <div className="flex justify-between items-center gap-3 text-sm">
              <span className="text-gray-600">Sans téléphone parent</span>
              <span className={elevesSansTelephone > 0 ? 'font-semibold text-orange-600 text-right' : 'font-semibold text-gray-700 text-right'}>
                {formatNumber(elevesSansTelephone)}
              </span>
            </div>
            <div className="border-t border-purple-200 pt-2 mt-2">
              <div className="flex justify-between items-center gap-3">
                <span className="text-gray-700 font-semibold">Élèves à relancer</span>
                <span className="text-xl font-bold text-purple-700 text-right">{formatNumber(elevesConcernes)}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">Un dossier sans téléphone ne peut pas être relancé par appel.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RecouvrementStatsCards;
